import { useCallback, useEffect, useRef, useState } from 'react';

import { Mic, MicOff, Minus, Phone, PhoneCall, VideoOff, Volume2 } from 'lucide-react';
import { createPortal } from 'react-dom';

import { operatorCallsApi } from '@/lib/api/operator-calls.api';
import { cn } from '@/lib/utils';
import { connectWebRtc } from '@/lib/webrtc';
import { useActiveCallStore } from '@/stores/active-call.store';

import type { ChannelConnectionState } from '@/hooks/useChannelSession';
import type { CallLifecycleStatus, WebRtcHandle } from '@/lib/webrtc';

// ── Helpers ────────────────────────────────────────────────────────

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

// ── Audio Waveform ────────────────────────────────────────────────

const WAVEFORM_BARS = 24;

const AudioWaveform = ({ active }: { active: boolean }) => {
  return (
    <div className="flex h-12 items-end justify-center gap-[3px]">
      {Array.from({ length: WAVEFORM_BARS }).map((_, i) => (
        <div
          key={i}
          className={cn(
            'w-[3px] rounded-full transition-all',
            active ? 'fcw-bar-active' : 'fcw-bar-idle',
          )}
          style={{
            animationDelay: `${i * 0.07}s`,
            backgroundColor: '#00a884',
          }}
        />
      ))}
    </div>
  );
};

// ── Animated Dots ─────────────────────────────────────────────────

const AnimatedDots = () => (
  <span className="inline-flex w-6 justify-start">
    <span className="fcw-dot-bounce" style={{ animationDelay: '0s' }}>
      .
    </span>
    <span className="fcw-dot-bounce" style={{ animationDelay: '0.2s' }}>
      .
    </span>
    <span className="fcw-dot-bounce" style={{ animationDelay: '0.4s' }}>
      .
    </span>
  </span>
);

// ── Wrapper ── renders nothing when no call is active, keys on callId
//               so the inner component remounts (resetting state) per call

export const FloatingCallWindow = () => {
  const isActive = useActiveCallStore((s) => s.isActive);
  const callId = useActiveCallStore((s) => s.callId);

  if (!isActive || !callId) return null;

  return <FloatingCallWindowInner key={callId} />;
};

// ── Inner component ───────────────────────────────────────────────

const FloatingCallWindowInner = () => {
  const contactName = useActiveCallStore((s) => s.contactName);
  const contactPhone = useActiveCallStore((s) => s.contactPhone);
  const callSid = useActiveCallStore((s) => s.callSid);
  const callId = useActiveCallStore((s) => s.callId);
  const channel = useActiveCallStore((s) => s.channel);
  const wsPath = useActiveCallStore((s) => s.wsPath);
  const wsToken = useActiveCallStore((s) => s.wsToken);
  const minimized = useActiveCallStore((s) => s.minimized);
  const position = useActiveCallStore((s) => s.position);
  const setMinimized = useActiveCallStore((s) => s.setMinimized);
  const setPosition = useActiveCallStore((s) => s.setPosition);
  const endCallStore = useActiveCallStore((s) => s.endCall);

  const [connectionState, setConnectionState] = useState<ChannelConnectionState>('connecting');
  // Actual call state on the contact's phone (from provider webhooks), which is
  // independent of our own WebRTC link to the server.
  const [callPhase, setCallPhase] = useState<CallLifecycleStatus>('ringing');
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [callError, setCallError] = useState<string | null>(null);
  const [callEnded, setCallEnded] = useState(false);

  const webRtcRef = useRef<WebRtcHandle | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Drag state
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Connect WebRTC on mount
  useEffect(() => {
    if (!wsPath || !wsToken || !callSid || !callId) return;

    const handle = connectWebRtc(
      wsPath,
      wsToken,
      callSid,
      callId,
      (state) => {
        console.log('[FloatingCallWindow] WebRTC state:', state);
        setConnectionState(state);
        // PSTN: the media stream (and thus our WebRTC bridge) only exists once
        // the callee answers, so a connected bridge means the call was answered.
        // WhatsApp: the bridge connects at initiation — pickup is signaled
        // separately via call_status ACCEPTED, so don't infer answer here.
        if (state === 'connected' && channel === 'pstn') {
          setCallPhase((phase) => (phase === 'ringing' ? 'answered' : phase));
        }
      },
      (msg) => setCallError(msg),
      () => {
        setCallEnded(true);
        setCallPhase((phase) => (phase === 'declined' ? phase : 'ended'));
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      },
      (status) => {
        console.log('[FloatingCallWindow] call status:', status);
        if (status === 'answered') {
          setCallPhase('answered');
        } else if (status === 'ringing') {
          setCallPhase((phase) => (phase === 'answered' ? phase : 'ringing'));
        } else {
          // declined or ended — the call is over
          setCallPhase(status);
          setCallEnded(true);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      },
    );

    webRtcRef.current = handle;

    return () => {
      if (webRtcRef.current) {
        webRtcRef.current.stop();
        webRtcRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [wsPath, wsToken, callSid, callId, channel]);

  // Duration timer — runs only while the contact has actually answered
  useEffect(() => {
    if (callPhase === 'answered' && !callEnded) {
      timerRef.current = setInterval(() => {
        setDuration((d) => d + 1);
      }, 1000);
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
    return undefined;
  }, [callPhase, callEnded]);

  const handleHangUp = useCallback(() => {
    setConnectionState('ending');

    // Tell the backend to hang up the real call (Twilio/Telnyx/Meta side) —
    // stopping only our WebRTC link would leave the contact's phone ringing.
    if (callSid) {
      operatorCallsApi.end(callSid).catch((err) => {
        console.error('[FloatingCallWindow] Failed to end call on server:', err);
      });
    }

    if (webRtcRef.current) {
      webRtcRef.current.stop();
      webRtcRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setCallEnded(true);
    setCallPhase('ended');
  }, [callSid]);

  const handleToggleMute = useCallback(() => {
    if (webRtcRef.current) {
      const isMuted = webRtcRef.current.toggleMute();
      setMuted(isMuted);
    }
  }, []);

  const handleClose = useCallback(() => {
    if (webRtcRef.current) {
      webRtcRef.current.stop();
      webRtcRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    endCallStore();
  }, [endCallStore]);

  // Drag handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Only drag from the header bar, not from buttons
      if ((e.target as HTMLElement).closest('button')) return;
      isDragging.current = true;
      dragOffset.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
      e.preventDefault();
    },
    [position],
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const newX = Math.max(0, Math.min(window.innerWidth - 100, e.clientX - dragOffset.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 48, e.clientY - dragOffset.current.y));
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      isDragging.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [setPosition]);

  const isRinging = callPhase === 'ringing' && !callEnded && connectionState !== 'error';
  const isConnected = callPhase === 'answered' && !callEnded;
  const wasDeclined = callPhase === 'declined';
  const name = contactName ?? 'Unknown';
  const phone = contactPhone ?? '';

  // ── Minimized bar ─────────────────────────────────────────────

  if (minimized) {
    return createPortal(
      <div
        className="fcw-minimized-bar"
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          zIndex: 9998,
          width: 320,
          height: 48,
          borderRadius: 12,
          cursor: 'grab',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
      >
        <div className="flex h-full items-center gap-3 px-4">
          {/* Green pulse dot */}
          {!callEnded && (
            <span className="fcw-pulse-dot inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-[#00a884]" />
          )}
          {callEnded && (
            <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-white/30" />
          )}

          {/* Contact name */}
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">{name}</span>

          {/* Duration */}
          {(isConnected || callEnded) && (
            <span className="shrink-0 font-mono text-xs text-white/70 tabular-nums">
              {formatDuration(duration)}
            </span>
          )}
          {isRinging && !callEnded && (
            <span className="shrink-0 text-xs text-amber-400">Ringing</span>
          )}

          {/* Mute toggle */}
          {!callEnded && (
            <button
              type="button"
              onClick={handleToggleMute}
              disabled={!isConnected}
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all',
                isConnected ? 'cursor-pointer' : 'cursor-not-allowed opacity-40',
                muted ? 'bg-red-500/30 text-red-400' : 'bg-white/10 text-white/70',
              )}
              title={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            </button>
          )}

          {/* Hang up / Close */}
          {!callEnded ? (
            <button
              type="button"
              onClick={handleHangUp}
              disabled={connectionState === 'ending'}
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-red-500 transition-all hover:bg-red-600"
              title="Hang up"
            >
              <Phone className="h-3.5 w-3.5 rotate-[135deg] text-white" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleClose}
              className="flex h-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/10 px-3 text-xs text-white/80 transition-all hover:bg-white/20"
              title="Close"
            >
              Close
            </button>
          )}
        </div>

        <style>{fcwStyles}</style>
      </div>,
      document.body,
    );
  }

  // ── Expanded window ─────────────────────────────────────────────

  return createPortal(
    <div
      className="fcw-expanded"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 9998,
        width: 380,
        borderRadius: 16,
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      {/* Draggable header bar */}
      <div
        className="fcw-header flex items-center justify-between px-4 py-2.5"
        style={{ cursor: 'grab' }}
        onMouseDown={handleMouseDown}
      >
        <span className="text-xs font-medium text-white/60">
          {callEnded ? (wasDeclined ? 'Call Declined' : 'Call Ended') : 'Active Call'}
        </span>
        <button
          type="button"
          onClick={() => setMinimized(true)}
          className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-white/50 transition-colors hover:bg-white/10 hover:text-white/80"
          title="Minimize"
        >
          <Minus className="h-4 w-4" />
        </button>
      </div>

      {/* Call content */}
      {!callEnded ? (
        <div className="fcw-call-active relative flex flex-col items-center px-8 pt-2 pb-8">
          {/* Recording indicator */}
          {isConnected && (
            <div className="flex items-center gap-2 rounded-full bg-black/30 px-3 py-1 backdrop-blur-sm">
              <span className="fcw-rec-dot inline-block h-2 w-2 rounded-full bg-red-500" />
              <span className="text-[11px] font-medium text-red-400">Recording</span>
            </div>
          )}

          {/* Spacer */}
          <div className="min-h-4 flex-1" />

          {/* Avatar with pulse ring */}
          <div className="relative mt-2">
            {isRinging && (
              <>
                <div className="fcw-pulse-ring absolute inset-0 rounded-full" />
                <div
                  className="fcw-pulse-ring absolute inset-0 rounded-full"
                  style={{ animationDelay: '0.75s' }}
                />
              </>
            )}
            <div
              className={cn(
                'relative z-10 flex h-24 w-24 items-center justify-center rounded-full transition-all duration-500',
                isConnected ? 'bg-[#00a884]/25' : 'bg-[#00a884]/15',
              )}
            >
              <span className="text-3xl font-bold text-white">{getInitials(name)}</span>
            </div>
            {isConnected && (
              <div className="absolute -right-1 -bottom-1 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-[#00a884]">
                <PhoneCall className="h-3 w-3 text-white" />
              </div>
            )}
          </div>

          {/* Contact name */}
          <h3 className="mt-5 text-center text-xl font-semibold text-white">{name}</h3>
          <p className="mt-1 text-sm text-white/50">{phone}</p>

          {/* Status */}
          <div
            className={cn(
              'mt-3 text-sm font-medium transition-colors',
              isConnected
                ? 'text-[#00a884]'
                : connectionState === 'error'
                  ? 'text-red-400'
                  : 'text-amber-400',
            )}
          >
            {isRinging && (
              <>
                Ringing
                <AnimatedDots />
              </>
            )}
            {isConnected && (connectionState === 'connected' ? 'Connected' : 'Reconnecting...')}
            {connectionState === 'ending' && 'Ending...'}
            {connectionState === 'error' && !isConnected && 'Error'}
          </div>

          {/* Duration timer */}
          {isConnected && (
            <div className="mt-2 font-mono text-3xl font-light tracking-wider text-white tabular-nums">
              {formatDuration(duration)}
            </div>
          )}

          {/* Audio waveform */}
          {isConnected && (
            <div className="mt-4">
              <AudioWaveform active={!muted} />
            </div>
          )}

          {/* Error message */}
          {callError && (
            <p className="mt-3 max-w-[260px] text-center text-xs text-red-400">{callError}</p>
          )}

          {/* Spacer */}
          <div className="min-h-4 flex-1" />

          {/* Bottom controls */}
          <div className="mt-6 flex items-center justify-center gap-5">
            {/* Mute */}
            <button
              type="button"
              onClick={handleToggleMute}
              disabled={!isConnected}
              className={cn(
                'flex h-14 w-14 flex-col items-center justify-center rounded-full transition-all',
                isConnected ? 'cursor-pointer' : 'cursor-not-allowed opacity-40',
                muted ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white/70',
              )}
              title={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>

            {/* Speaker */}
            <button
              type="button"
              disabled
              className="flex h-14 w-14 cursor-not-allowed flex-col items-center justify-center rounded-full bg-white/10 text-white/40 opacity-40"
              title="Speaker (coming soon)"
            >
              <Volume2 className="h-5 w-5" />
            </button>

            {/* Video placeholder */}
            <button
              type="button"
              disabled
              className="flex h-14 w-14 cursor-not-allowed flex-col items-center justify-center rounded-full bg-white/10 text-white/40 opacity-40"
              title="Video (coming soon)"
            >
              <VideoOff className="h-5 w-5" />
            </button>

            {/* Hang up */}
            <button
              type="button"
              onClick={handleHangUp}
              disabled={connectionState === 'ending'}
              className={cn(
                'flex h-16 w-16 items-center justify-center rounded-full bg-red-500 shadow-lg shadow-red-500/30 transition-all',
                connectionState === 'ending'
                  ? 'cursor-not-allowed opacity-60'
                  : 'cursor-pointer hover:bg-red-600 hover:shadow-red-500/50',
              )}
              title="Hang up"
            >
              <Phone className="h-6 w-6 rotate-[135deg] text-white" />
            </button>
          </div>
        </div>
      ) : (
        /* ── Call Ended ──────────────────────────────────── */
        <div className="fcw-call-ended flex flex-col items-center px-8 pt-6 pb-8">
          {/* Avatar dimmed */}
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/5 opacity-60">
            <span className="text-2xl font-bold text-white/60">{getInitials(name)}</span>
          </div>

          <h3 className="mt-5 text-xl font-semibold text-white/80">
            {wasDeclined ? 'Call Declined' : 'Call Ended'}
          </h3>
          <p className="mt-2 text-sm text-white/40">
            {wasDeclined
              ? 'The contact declined the call'
              : `Duration: ${formatDuration(duration)}`}
          </p>

          {callError && <p className="mt-3 text-center text-xs text-red-400">{callError}</p>}

          <button
            type="button"
            onClick={handleClose}
            className="mt-8 w-full cursor-pointer rounded-xl bg-white/10 px-4 py-3 text-sm font-semibold text-white/90 transition-all hover:bg-white/15"
          >
            Close
          </button>
        </div>
      )}

      <style>{fcwStyles}</style>
    </div>,
    document.body,
  );
};

// ── Styles ──────────────────────────────────────────────────────────

const fcwStyles = `
  /* ── Floating call window ──────────────────────────── */
  .fcw-expanded {
    background: linear-gradient(180deg, #0b3d2e 0%, #0a1e14 40%, #111b21 100%);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  }

  .fcw-header {
    background: rgba(0, 0, 0, 0.2);
  }

  .fcw-call-active {
    min-height: 440px;
  }

  .fcw-call-ended {
    background: linear-gradient(180deg, #1a2730 0%, #111b21 100%);
  }

  .fcw-minimized-bar {
    background: linear-gradient(135deg, #0b3d2e 0%, #111b21 100%);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  }

  /* ── Pulsing avatar ring (ringing state) ──────────── */
  .fcw-pulse-ring {
    border: 2px solid #00a884;
    animation: fcw-pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  @keyframes fcw-pulse {
    0% { transform: scale(1); opacity: 0.6; }
    100% { transform: scale(1.8); opacity: 0; }
  }

  /* ── Green dot pulse (minimized bar) ─────────────── */
  .fcw-pulse-dot {
    animation: fcw-dot-pulse 2s ease-in-out infinite;
  }
  @keyframes fcw-dot-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }

  /* ── Recording dot pulse ──────────────────────────── */
  .fcw-rec-dot {
    animation: fcw-rec-blink 1.5s ease-in-out infinite;
  }
  @keyframes fcw-rec-blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }

  /* ── Animated dots ("Ringing...") ─────────────────── */
  .fcw-dot-bounce {
    animation: fcw-dot-bounce 1.2s ease-in-out infinite;
    display: inline-block;
  }
  @keyframes fcw-dot-bounce {
    0%, 80%, 100% { opacity: 0.2; }
    40% { opacity: 1; }
  }

  /* ── Audio waveform bars ──────────────────────────── */
  .fcw-bar-active {
    animation: fcw-wave 0.8s ease-in-out infinite alternate;
  }
  .fcw-bar-idle {
    animation: fcw-wave-idle 1.6s ease-in-out infinite alternate;
  }
  @keyframes fcw-wave {
    0% { height: 4px; }
    100% { height: 32px; }
  }
  @keyframes fcw-wave-idle {
    0% { height: 3px; }
    100% { height: 8px; }
  }
`;
